/** XeAgents.ai**/
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AlertCircle, Loader2, Sparkles, Wand2 } from "lucide-react";

/**
 * Ask the AI for this field, and say what you want.
 *
 * This replaces a button labelled "Regenerate", which promised "do that again"
 * and was read by everyone who had typed in the box as "do what I just asked".
 * One user wrote "Create Unique Value Proposition of VoicePro from Non Banking
 * Financial Sector Perspective" into the textarea and pressed it; the call
 * succeeded, ignored every word of that, and then overwrote it. Nothing on the
 * page accepted an instruction, so the mistake was the reasonable one.
 *
 * The dialog is also where the overwrite is consented to. An earlier version
 * showed a bare "your edits will be replaced" confirmation at exactly this
 * moment - the same interruption, doing nothing useful. Asking what should
 * change turns the warning into the reason the dialog exists.
 */
export function RewriteButton({
  fieldLabel,
  currentText,
  isEdited,
  busy,
  disabled,
  onRewrite,
}: {
  fieldLabel: string;
  currentText: string;
  /** The current text is the user's own work, so replacing it is worth naming. */
  isEdited: boolean;
  busy?: boolean;
  disabled?: boolean;
  onRewrite: (instruction: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [instruction, setInstruction] = useState("");

  const hasContent = currentText.trim().length > 0;

  const submit = () => {
    setOpen(false);
    onRewrite(instruction.trim());
    setInstruction("");
  };

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-7 gap-1.5 px-2 text-xs font-normal text-muted-foreground hover:text-foreground"
        disabled={disabled || busy}
        onClick={() => setOpen(true)}
        data-testid={`button-rewrite-${fieldLabel}`}
      >
        {busy ? (
          <><Loader2 className="h-3.5 w-3.5 animate-spin" />Writing</>
        ) : (
          <><Wand2 className="h-3.5 w-3.5" />{hasContent ? "Rewrite with AI" : "Write with AI"}</>
        )}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{hasContent ? "Rewrite" : "Write"} {fieldLabel.toLowerCase()}</DialogTitle>
            <DialogDescription>
              Say what you want and the AI will write it from your website and product details.
              Leave it blank to simply try again.
            </DialogDescription>
          </DialogHeader>

          {hasContent && (
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground">It currently says</p>
              {/* Shown, not editable: this dialog is for saying what should
                  change. The field itself is right behind it for direct edits. */}
              <p className="max-h-28 overflow-y-auto rounded-md border bg-muted/40 p-2.5 text-xs leading-relaxed text-foreground/80">
                {currentText}
              </p>
            </div>
          )}

          <div className="space-y-1.5">
            <label htmlFor="rewrite-instruction" className="text-sm font-medium">
              What should change?
              <span className="ml-2 text-xs font-normal text-muted-foreground">Optional</span>
            </label>
            <Textarea
              id="rewrite-instruction"
              rows={3}
              value={instruction}
              onChange={(event) => setInstruction(event.target.value)}
              placeholder="e.g. write it for non-banking financial companies, and keep it under 20 words"
              className="text-sm"
              autoFocus
              data-testid="input-rewrite-instruction"
            />
            <p className="text-xs text-muted-foreground">
              The AI can change the angle, emphasis, length or wording — but it will not claim
              anything your website and details do not support.
            </p>
          </div>

          {isEdited && hasContent && (
            <p className="flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400">
              <AlertCircle className="mt-px h-3.5 w-3.5 shrink-0" />
              This text is yours — rewriting replaces it.
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={submit} data-testid="button-confirm-rewrite">
              <Sparkles className="mr-2 h-4 w-4" />
              {hasContent ? "Rewrite" : "Write"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
